import { useState, useEffect, useMemo, useRef } from 'react'
import { updateUserProfile } from '../services/userService'
import {
  mergeAvatarPresets,
  subscribeAvatarPresetsExtra,
  addAvatarPresetUrl,
  removeAvatarPresetById,
  normalizePresetUrl
} from '../services/avatarPresetsService'
import {
  getVisibleAvatarCommonsSuggestions,
  isAvatarSuggestionPoolFullyUsed
} from '../data/avatarCommonsSearchSuggestions'
import { resolveCommonsThumbCandidatesForUrls } from '../services/commonsThumbResolveService'
import { buildAvatarPresetDisplayCandidates } from '../utils/commonsImageUrls'
import { getGoogleProviderPhotoURL, isSameGoogleProfilePhoto } from '../utils/googleProviderPhoto'
import './ProfileAvatarPicker.css'

const COMMONS_SEARCH = 'https://commons.wikimedia.org/wiki/Special:Search?search='

/**
 * Limite de variantes par tuile : sans plafond, chaque onError enchaîne ~10+ URLs Commons
 * Grille admin-only = peu de tuiles ; un peu plus de replis pour couvrir les largeurs réellement servies par Commons.
 */
const MAX_AVATAR_DISPLAY_CANDIDATES = 8

/** Chaîne d’URL pour la grille : API + quelques repliers seulement (évite tempête réseau). */
function getPresetDisplayCandidates(p, apiCandidates) {
  return buildAvatarPresetDisplayCandidates(p.url, apiCandidates, {
    max: MAX_AVATAR_DISPLAY_CANDIDATES
  })
}

/** @param {{ variant: 'modal' | 'page', user, userData, isAdminTeacher, disabled?: boolean, showTitle?: boolean, onAfterPick?: () => void }} props */
export default function ProfileAvatarPicker({
  variant,
  user,
  userData,
  isAdminTeacher,
  disabled = false,
  showTitle = true,
  onAfterPick
}) {
  const [extra, setExtra] = useState([])
  const [photoSaving, setPhotoSaving] = useState(false)
  const [adminUrlInput, setAdminUrlInput] = useState('')
  const [adminMsg, setAdminMsg] = useState(null)
  const [adminBusy, setAdminBusy] = useState(false)
  const [adminPanelOpen, setAdminPanelOpen] = useState(false)
  const [thumbFallbackIdx, setThumbFallbackIdx] = useState({})
  const [thumbResolveMap, setThumbResolveMap] = useState(() => new Map())
  const [pendingSuggestionId, setPendingSuggestionId] = useState(null)

  const showAdminLibrary =
    Boolean(isAdminTeacher && userData?.role === 'teacher')

  useEffect(() => {
    if (disabled || !user?.uid) return undefined
    return subscribeAvatarPresetsExtra(setExtra)
  }, [disabled, user?.uid])

  const merged = useMemo(() => mergeAvatarPresets(extra), [extra])

  const visibleCommonsSuggestions = useMemo(
    () => getVisibleAvatarCommonsSuggestions(extra),
    [extra]
  )
  const allCommonsSuggestionsCovered = useMemo(
    () => isAvatarSuggestionPoolFullyUsed(extra),
    [extra]
  )

  const commonsUrlKey = useMemo(
    () => [...new Set(merged.map((p) => p.url))].sort().join('\n'),
    [merged]
  )

  const thumbProgressRafRef = useRef(null)

  useEffect(() => {
    const urls = [
      ...new Set(
        merged
          .map((p) => p.url)
          .filter(
            (u) =>
              u.includes('upload.wikimedia.org/wikipedia/commons/') && !u.includes('/thumb/')
          )
      )
    ]
    if (urls.length === 0) return undefined
    let cancelled = false
    const flushProgress = (map) => {
      if (cancelled) return
      if (thumbProgressRafRef.current != null) {
        cancelAnimationFrame(thumbProgressRafRef.current)
        thumbProgressRafRef.current = null
      }
      thumbProgressRafRef.current = requestAnimationFrame(() => {
        thumbProgressRafRef.current = null
        if (!cancelled) setThumbResolveMap(new Map(map))
      })
    }
    resolveCommonsThumbCandidatesForUrls(urls, { onProgress: flushProgress }).then((map) => {
      if (cancelled) return
      if (thumbProgressRafRef.current != null) {
        cancelAnimationFrame(thumbProgressRafRef.current)
        thumbProgressRafRef.current = null
      }
      setThumbResolveMap(new Map(map))
    })
    return () => {
      cancelled = true
      if (thumbProgressRafRef.current != null) {
        cancelAnimationFrame(thumbProgressRafRef.current)
        thumbProgressRafRef.current = null
      }
    }
  }, [commonsUrlKey])

  useEffect(() => {
    setThumbFallbackIdx({})
  }, [commonsUrlKey])

  const profilePhotoURL = userData?.photoURL ?? user?.photoURL ?? ''
  const currentNorm = profilePhotoURL ? normalizePresetUrl(profilePhotoURL) : ''
  const googlePhotoUrl = getGoogleProviderPhotoURL(user)
  const showGoogleTile = Boolean(googlePhotoUrl)

  const initials = (userData?.displayName || user?.displayName || 'U').charAt(0).toUpperCase()

  const pickPhoto = async (url) => {
    if (!user?.uid || disabled) return
    setPhotoSaving(true)
    try {
      await updateUserProfile(user.uid, { photoURL: url })
      // Pas de refreshUserData : onSnapshot users/{uid} met à jour photoURL après updateDoc.
      onAfterPick?.()
    } catch (err) {
      console.error('pickPhoto:', err)
    } finally {
      setPhotoSaving(false)
    }
  }

  const handleAddPreset = async () => {
    setAdminMsg(null)
    setAdminBusy(true)
    try {
      await addAvatarPresetUrl(adminUrlInput, {
        suggestionId: pendingSuggestionId || undefined
      })
      setAdminUrlInput('')
      setPendingSuggestionId(null)
      setAdminMsg({ type: 'ok', text: 'Image ajoutée à la bibliothèque.' })
    } catch (e) {
      setAdminMsg({ type: 'err', text: e?.message || 'Impossible d’ajouter cette URL.' })
    } finally {
      setAdminBusy(false)
    }
  }

  const handleRemovePreset = async (id) => {
    setAdminMsg(null)
    setAdminBusy(true)
    try {
      await removeAvatarPresetById(id)
      setAdminMsg({ type: 'ok', text: 'Image retirée de la bibliothèque.' })
    } catch (e) {
      setAdminMsg({ type: 'err', text: e?.message || 'Impossible de retirer cette entrée.' })
    } finally {
      setAdminBusy(false)
    }
  }

  const rootClass =
    variant === 'page'
      ? 'profile-avatar-picker profile-avatar-picker--page'
      : 'profile-avatar-picker profile-avatar-picker--modal'

  if (disabled || !user?.uid) return null

  const initialsSelected = !profilePhotoURL
  const googleSelected = showGoogleTile && isSameGoogleProfilePhoto(profilePhotoURL, googlePhotoUrl)

  return (
    <div className={rootClass}>
      {showTitle && <h4 className="profile-avatar-picker-title">Choisir une image de profil</h4>}

      {merged.length === 0 && (
        <p className="profile-avatar-picker-empty-library" role="status">
          Aucun avatar proposé pour l’instant. Vous pouvez utiliser vos initiales
          {showGoogleTile ? ' ou votre photo Google' : ''}. Un administrateur peut enrichir la bibliothèque
          Wikimedia Commons.
        </p>
      )}

      <div className="profile-avatar-picker-grid-scroll">
        <div className="profile-avatar-picker-grid" role="list">
          <button
            type="button"
            role="listitem"
            className={`profile-avatar-picker-tile profile-avatar-picker-tile--initials${
              initialsSelected ? ' profile-avatar-picker-tile--selected' : ''
            }`}
            onClick={() => pickPhoto(null)}
            disabled={photoSaving}
            title="Afficher les initiales"
            aria-label="Afficher les initiales à la place d’une photo"
          >
            {initials}
          </button>

          {showGoogleTile && (
            <button
              type="button"
              role="listitem"
              className={`profile-avatar-picker-tile${googleSelected ? ' profile-avatar-picker-tile--selected' : ''}`}
              onClick={() => pickPhoto(googlePhotoUrl)}
              disabled={photoSaving}
              title="Photo du compte Google"
              aria-label="Utiliser la photo du compte Google"
            >
              <img
                src={googlePhotoUrl}
                alt=""
                width={52}
                height={52}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </button>
          )}

          {merged.map((p, presetIndex) => {
            const selected = currentNorm && normalizePresetUrl(p.url) === currentNorm
            const resolved = thumbResolveMap.get(p.url)?.thumbCandidates
            const candidates = getPresetDisplayCandidates(p, resolved)
            const fi = thumbFallbackIdx[p.id] ?? 0
            const imgSrc = fi < candidates.length ? candidates[fi] : null
            const showRemove = showAdminLibrary && adminPanelOpen
            const globalIndex = presetIndex + (showGoogleTile ? 2 : 1)

            return (
              <div key={p.id} className="profile-avatar-picker-tile-wrap" role="listitem">
                <button
                  type="button"
                  className={`profile-avatar-picker-tile${selected ? ' profile-avatar-picker-tile--selected' : ''}`}
                  onClick={() => pickPhoto(p.url)}
                  disabled={photoSaving}
                  title={p.label || 'Choisir cet avatar'}
                  aria-label={p.label || 'Choisir cet avatar'}
                >
                  {imgSrc ? (
                    <img
                      key={`${p.id}-${fi}`}
                      src={imgSrc}
                      alt=""
                      width={52}
                      height={52}
                      loading={globalIndex < 8 ? 'eager' : 'lazy'}
                      fetchPriority={globalIndex < 4 ? 'high' : undefined}
                      decoding="async"
                      onError={() =>
                        setThumbFallbackIdx((prev) => ({
                          ...prev,
                          [p.id]: (prev[p.id] ?? 0) + 1
                        }))
                      }
                    />
                  ) : (
                    <span className="profile-avatar-picker-tile-fallback" title="Miniature indisponible">
                      ?
                    </span>
                  )}
                </button>
                {showRemove && (
                  <button
                    type="button"
                    className="profile-avatar-picker-tile-remove"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleRemovePreset(p.id)
                    }}
                    disabled={adminBusy}
                    title="Retirer de la bibliothèque"
                    aria-label={`Retirer « ${p.label || 'cette image'} » de la bibliothèque`}
                  >
                    ×
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <p className="profile-avatar-picker-hint">
        Images proposées sous licence libre (Wikimedia Commons). Pas d’upload depuis l’app.
      </p>

      {showAdminLibrary && (
        <details
          className="profile-avatar-picker-admin"
          onToggle={(e) => setAdminPanelOpen(e.currentTarget.open)}
        >
          <summary>Gérer la bibliothèque d’avatars (admin)</summary>
          <div className="profile-avatar-picker-admin-body">
            {visibleCommonsSuggestions.length > 0 && (
              <div className="profile-avatar-picker-admin-suggestions">
                <p className="profile-avatar-picker-hint" style={{ margin: '0 0 0.5rem' }}>
                  Suggestions de recherche (cliquez puis collez l’URL du fichier ci-dessous pour marquer la
                  suggestion comme couverte) :
                </p>
                <ul className="profile-avatar-picker-admin-suggestion-list">
                  {visibleCommonsSuggestions.map((s) => (
                    <li key={s.id}>
                      <a
                        className="profile-avatar-picker-admin-link"
                        href={`${COMMONS_SEARCH}${encodeURIComponent(s.query)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setPendingSuggestionId(s.id)}
                      >
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {allCommonsSuggestionsCovered && (
              <p className="profile-avatar-picker-hint" role="status">
                Toutes les suggestions de la liste sont déjà couvertes par la bibliothèque.
              </p>
            )}
            {pendingSuggestionId && (
              <p className="profile-avatar-picker-hint" role="status">
                Prochain ajout sera associé à la suggestion sélectionnée (jusqu’à validation ou nouveau clic).
              </p>
            )}
            <a
              className="profile-avatar-picker-admin-link"
              href={`${COMMONS_SEARCH}${encodeURIComponent('music portrait')}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Recherche libre sur Wikimedia Commons (nouvel onglet)
            </a>
            <p className="profile-avatar-picker-hint" style={{ margin: 0 }}>
              Sur la page du fichier, copiez le lien direct du fichier (commence par{' '}
              <code style={{ fontSize: '0.75rem' }}>https://upload.wikimedia.org/wikipedia/commons/</code>
              ), pas l’URL de la page Commons.
            </p>
            <div className="profile-avatar-picker-admin-row">
              <input
                type="url"
                className="profile-avatar-picker-admin-input"
                value={adminUrlInput}
                onChange={(e) => setAdminUrlInput(e.target.value)}
                placeholder="Coller l’URL du fichier…"
                disabled={adminBusy}
                aria-label="URL Wikimedia Commons"
              />
              <button
                type="button"
                className="profile-avatar-picker-admin-add"
                onClick={handleAddPreset}
                disabled={adminBusy || !adminUrlInput.trim()}
              >
                {adminBusy ? '…' : 'Ajouter à la liste'}
              </button>
            </div>
            {adminMsg && (
              <p
                className={`profile-avatar-picker-admin-msg profile-avatar-picker-admin-msg--${adminMsg.type === 'err' ? 'err' : 'ok'}`}
                role="status"
              >
                {adminMsg.text}
              </p>
            )}
            {adminPanelOpen && extra.length > 0 && (
              <p className="profile-avatar-picker-hint" style={{ margin: 0 }}>
                Les images ajoutées à la plateforme ont une croix (×) sur la vignette ci-dessus.
              </p>
            )}
          </div>
        </details>
      )}
    </div>
  )
}
